import { z } from "zod";

/**
 * How a slide is presented and interacted with, independent of the language skill it
 * trains. The same template serves many skills — a `videoQuestion` may be grammar,
 * listening or speaking — so the pedagogical dimension lives on `skill`, not here.
 *
 * Supersedes discriminating on the legacy `type` + `knowledgeArea` pair.
 */
export const SlideTemplateEnum = z.enum([
  "vocabulary",
  "passageReading",
  "spellingToSound",
  "soundToSpelling",
  "listenRepeat",
  "freeformCanvas",
  "wordCanvas",
  "conversation",
  "videoQuestion",
  "handWriting",
  /** Audio autoplays and the text reveals on completion, checked against a correct answer. */
  "audioRevealPrompt",
]);

export type SlideTemplate = z.infer<typeof SlideTemplateEnum>;

/**
 * Legacy `type`/`knowledgeArea` pairs mapped onto their template. Deliberately
 * behaviour-preserving: every pair resolves to whatever component renders it today,
 * so backfilled slides render identically. Pairs with no runtime renderer (the
 * "Coming Soon" placeholders, and `usage` everywhere) are absent by design.
 */
const TEMPLATE_BY_LEGACY_PAIR: Record<string, Record<string, SlideTemplate>> = {
  word: {
    pronunciation: "vocabulary",
    vocabulary: "listenRepeat",
    meaning: "wordCanvas",
  },
  grammar: {
    form: "conversation",
    sentence: "audioRevealPrompt",
    response: "videoQuestion",
  },
  reading: {
    fluency: "passageReading",
    // Renders as the fluency component today; kept identical until comprehension is built.
    comprehension: "passageReading",
  },
  phonics: {
    encode: "soundToSpelling",
    decode: "spellingToSound",
  },
  other: {
    reading: "freeformCanvas",
    writing: "freeformCanvas",
    listening: "freeformCanvas",
    speaking: "freeformCanvas",
    // Dropped from OtherSlideSchema but may still exist in older data.
    vocabulary: "freeformCanvas",
  },
};

/**
 * The pair a template writes back as, for callers that must still populate the legacy fields.
 * Several templates absorb more than one pair (freeformCanvas covers four "other" areas), so this
 * names the CANONICAL one — the value authoring writes — rather than inverting the map above.
 */
export const LEGACY_PAIR_BY_TEMPLATE: Record<
  SlideTemplate,
  { type: string; knowledgeArea: string }
> = {
  vocabulary: { type: "word", knowledgeArea: "pronunciation" },
  listenRepeat: { type: "word", knowledgeArea: "vocabulary" },
  wordCanvas: { type: "word", knowledgeArea: "meaning" },
  conversation: { type: "grammar", knowledgeArea: "form" },
  audioRevealPrompt: { type: "grammar", knowledgeArea: "sentence" },
  videoQuestion: { type: "grammar", knowledgeArea: "response" },
  passageReading: { type: "reading", knowledgeArea: "fluency" },
  soundToSpelling: { type: "phonics", knowledgeArea: "encode" },
  spellingToSound: { type: "phonics", knowledgeArea: "decode" },
  // Authoring has only ever written "reading"; writing/listening/speaking exist in old data.
  freeformCanvas: { type: "other", knowledgeArea: "reading" },
  handWriting: { type: "other", knowledgeArea: "writing" },
};

/** Templates rendered as a drag-and-drop canvas of elements rather than a fixed layout. */
export function isFreeformTemplate(template: SlideTemplate | null | undefined): boolean {
  return template === "freeformCanvas" || template === "wordCanvas";
}

/** Returns null for pairs with no renderer, letting callers keep their existing fallback. */
export function deriveSlideTemplate(
  type: string | null | undefined,
  knowledgeArea: string | null | undefined
): SlideTemplate | null {
  if (!type || !knowledgeArea) return null;
  return TEMPLATE_BY_LEGACY_PAIR[type]?.[knowledgeArea] ?? null;
}

type TemplateResolvable = {
  template?: SlideTemplate | null | undefined;
  type?: string | null | undefined;
  knowledgeArea?: string | null | undefined;
};

/**
 * Single read path during the migration: prefer the stored template, fall back to
 * deriving it. Every new `template` consumer must go through this until the backfill
 * is verified and `type`/`knowledgeArea` are dropped.
 */
export function resolveSlideTemplate(
  slide: TemplateResolvable | null | undefined
): SlideTemplate | null {
  if (!slide) return null;
  return slide.template ?? deriveSlideTemplate(slide.type, slide.knowledgeArea);
}
